import store from "@src/Redux/Store/store";
import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import ActiveIcon from '../../../public/assets/Active.svg';
import AddActiveIcon from '../../../public/assets/add-active.svg';
import AddIcon from '../../../public/assets/add.svg';
import AdvancedSvg from '../../../public/assets/advanced.svg';
import AppManagementSvg from '../../../public/assets/app-management-active.svg';
import AttachActiveIcon from '../../../public/assets/attach-active.svg';
import AttachIcon from '../../../public/assets/attach.svg';
import CloneActiveIcon from '../../../public/assets/clone-active.svg';
import CloneIcon from '../../../public/assets/clone.svg';
import DecrementBlueSvg from '../../../public/assets/decrement-blue.svg';
import DecrementGreenSvg from '../../../public/assets/decrement-green.svg';
import DecrementRedSvg from '../../../public/assets/decrement-red.svg';
import EnterpriseSvg from '../../../public/assets/enterprise.svg';
import Error401Icon from '../../../public/assets/error-401.svg';
import Error404Icon from '../../../public/assets/error-404.svg';
import Error500Icon from '../../../public/assets/error-500.svg';
import GenericErrorIcon from '../../../public/assets/genericError.svg';
import InactiveIcon from '../../../public/assets/Inactive.svg';
import IncrementGreenSvg from '../../../public/assets/increment-green.svg';
import IncrementRedSvg from '../../../public/assets/increment-red.svg';
import NeutralIconSvg from '../../../public/assets/neutral.svg';
import StandardSvg from '../../../public/assets/standard.svg';
import SuspendedIcon from '../../../public/assets/Suspended.svg';
import TryAndBuy from '../../../public/assets/tryAndBuy.svg';
import { IPolicyBlockProps } from '../../_components/atoms/DraggablePolicyBlock';
import { ApplicationSelectCardProps } from '../../_components/molecules/ApplicationSelectCard';
import { IDropdownOptionProps } from '../../_components/molecules/Dropdown';
import AuthService from '../../api-service/auth-service/AuthService';
import UserGroupService from '../../api-service/user-group-service/UserGroupService';
import AppService, {
  IAddOrRemoveAppsToUserRequestBody,
} from '../../api-service/app-service/AppService';

import {
  ACCOUNT_SETTING_TIMEZONE_OPTIONS,
  ADMIN,
  SECONDARY_ADMIN,
  ADMIN_SIDE_NAVIGATION_ITEMS,
  ALL_USERS_GROUP,
  APPLICATION_PROTOCOLS,
  APPLICATION_TYPE_OPTIONS,
  COMMON_SHADES,
  END_USER_SIDE_NAVIGATION_ITEMS,
  FULL_NAME,
  IDropDownItem,
  ITooltipValidationParams,
  POLICIES_HEADERS,
  POLICIES_HEADERS_UNDER_USERGROUPS,
  POLICIES_HEADERS_UNDER_USERS,
  REGEX,
  ROLE,
  SIDE_NAVIGATION_END_ITEMS,
  SOURCE,
  TIME_ZONE_MAP,
  USER_ID
} from '../constants';
import { FAQType, HelpFAQType } from '../helpFAQs';
import { OperationTypes } from '../Interfaces/entityInterfaces';
import errorMessages from '../messages/error';
import { EXTRA_COLORS } from '../theme';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.extend(relativeTime);


export const ALLOWED_IMAGE_MIME_TYPES = ["image/png", "image/jpg","image/jpeg", "image/webp", "image/svg+xml", "image/svg", "image/gif", "image/bmp"];

export const getColorForText = (text: string) => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash += text.charCodeAt(i);
  }
  const keys = Object.values(COMMON_SHADES);
  return keys[hash % keys.length];
};

export const checkPassword = (
  password: string,
  passwordParameters: ITooltipValidationParams[],
) => {
  passwordParameters[0].satisfied = checkPasswordLength(password);
  passwordParameters[1].satisfied = passwordHasUppercase(password);
  passwordParameters[2].satisfied = passwordHasDigits(password);
  return passwordParameters;
};

export const validateURLWithParameters = (
  url: string,
  urlValidationParameters: ITooltipValidationParams[],
) => {
  if (url === '') {
    urlValidationParameters[0].satisfied = false;
  } else {
    urlValidationParameters[0].satisfied = !containsSpace(url);
  }
  return urlValidationParameters;
};

export const validateProperUrl = (url: string) => {
  if (url && url.length > 0) {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  }
  return false;
}

export const validateURL = (url: string) => {
  return !containsSpace(url);
};

export const checkPasswordLength = (password: string) => {
  return password.length >= 6;
};

export const passwordHasUppercase = (password: string) => {
  return /[A-Z]/.test(password);
};

export const passwordHasDigits = (password: string) => {
  return /\d/.test(password);
};

export const containsSpace = (value: string) => {
  return REGEX.SPACE.test(value);
};

export const validateDomainUrl = (url: string) => {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) return false;
  const domainPattern = new RegExp(REGEX.DOMAIN_URL);
  const ipAddressPattern = new RegExp(REGEX.IP_ADDRESS_URL);
  return domainPattern.test(url) || ipAddressPattern.test(url);
};

export const validateLdapUrl = (url: string) => {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) return false;

  if (!trimmedUrl.toLowerCase().startsWith('ldap://')) return false;

  const domainPart = trimmedUrl.substring(7);

  if (!domainPart) return false;

  const hasPort = domainPart.includes(':');

  if (hasPort) {
    const [domain, port] = domainPart.split(':');

    const portNumber = parseInt(port, 10);
    const isValidPort = !isNaN(portNumber) && portNumber >= 1 && portNumber <= 65535;

    if (!isValidPort) return false;

    const domainPattern = new RegExp(REGEX.DOMAIN_URL);
    const ipAddressPattern = new RegExp(REGEX.SERVER_IP);

    return domainPattern.test(domain) || ipAddressPattern.test(domain);
  } else {
    const domainPattern = new RegExp(REGEX.DOMAIN_URL);
    const ipAddressPattern = new RegExp(REGEX.SERVER_IP);

    return domainPattern.test(domainPart) || ipAddressPattern.test(domainPart);
  }
};

/**
 * Validates for empty value
 * @param value Value to be checked if it is empty or not in string format
 * @returns true if value is null/undefined/empty/has only whitespaces, false if value is not empty
 */
export const isEmptyValidation = (value: string) => {
  return !value || value.length === 0;
};

export const isObjectValid = (object: object) => {
  return object && Object.keys(object).length !== 0;
};

/**
 * Validates email against regex and returns the result
 * @param email Email to be validated in string format
 * @returns true if valid email. false if not
 */
export const isEmailValid = (email: string) => {
  return REGEX.EMAIL.test(String(email));
};

/**
 * Validates if password matches all requirements
 * @param password: Password to be validated in string format
 * @returns true is valid password, false if not
 */
export const isPasswordValid = (password: string) => {
  return (
    checkPasswordLength(password) &&
    passwordHasUppercase(password) &&
    passwordHasDigits(password)
  );
};

export const validatePhone = (organisationPhone: string) => {
  if (isEmptyValidation(organisationPhone)) {
    return false;
  }
  const pattern = new RegExp(REGEX.PHONE);
  return pattern.test(organisationPhone);
};

export const getAvatarName = (username: string | null) => {
  if (!username) {
    return '';
  }
  const names = username.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export const getStatusIcon = (status: string) => {
  if (status.toLowerCase() === 'active') {
    return ActiveIcon;
  }
  if (status.toLowerCase() === 'inactive') {
    return InactiveIcon;
  }
  if (status.toLowerCase() === 'suspended') {
    return SuspendedIcon;
  }
};

export const getChipsWithUserGroupNames = (
  usergroups: Array<{
    id?: number;
    usergroupname: string;
  }>,
) => {
  const labels: Array<{
    label: string;
  }> = [];
  usergroups.forEach((usergroup) => {
    labels.push({ label: usergroup.usergroupname });
  });
  return labels;
};

/**
 * Returns Domain name from url
 * @param url URL from which domain name has to be extracted
 * @returns domain name if a valid url, empty string if not
 * ex: https://github.com/Sonet/pull/12
 */
export const getDomainNameFromURL = (url: string = "") => {
  if (!url) return '';
  try {
    const domain = new URL(`${url?.startsWith('http') ? '' : 'https://'}${url}`);
    if (!url || !domain) {
      return '';
    }
    return domain.hostname;
  } catch (exception) {
    // Exception is thrown if invalid url is provided
    return '';
  }
};
export const avatarcomponentlist = (
  users: Array<{
    id: number;
    username: string;
    profileImage?: string;
  }>,
) => {
  const avatarlist: string[] = [];
  users.forEach((user) => {
    user.profileImage
      ? avatarlist.push(user.profileImage)
      : avatarlist.push(getAvatarName(user.username));
  });
  return avatarlist;
};

export const getChipsWithViolationType = (typeOfViolations: Array<string>) => {
  const labels: Array<{
    label: string;
    shade: 'blue' | 'green' | 'orange' | 'red' | 'violet' | 'grey' | undefined;
  }> = [];
  typeOfViolations.forEach((type) => {
    const returnedObject = getColorForText(type);
    labels.push({
      label: type,
      shade: getColorFromHex(returnedObject['main']),
    });
  });
  return labels;
};

export const getAppsOrGroupsForTable = (
  appsOrGroups: string[],
  showAppsOrGroupsCount: number,
) => {
  let requiredAppsOrGroups = '';
  if (appsOrGroups.length <= showAppsOrGroupsCount) {
    requiredAppsOrGroups = appsOrGroups.join(', ');
  } else {
    let slicedAppsOrGroups = appsOrGroups.slice(0, showAppsOrGroupsCount);
    requiredAppsOrGroups = slicedAppsOrGroups.join(', ');
    let countToShow = appsOrGroups.length - showAppsOrGroupsCount;
    let appsOrGroupsCountToShow = `, +${countToShow.toString()}`;
    requiredAppsOrGroups = requiredAppsOrGroups + appsOrGroupsCountToShow;
  }
  return requiredAppsOrGroups;
};

export const getUpdatedByForPolicies = (updatedBy: {
  dateTime: string;
  name: string;
}) => {
  let requiredDate = getDateAndTimeFromISO(updatedBy.dateTime).date.replace(
    '/',
    ',',
  );
  let requiredTime = getDateAndTimeFromISO(updatedBy.dateTime).time;
  return `${requiredDate} ${requiredTime}`;
};

export const getColorFromHex = (hex: string) => {
  switch (hex) {
    case EXTRA_COLORS.blue:
      return 'blue';
    case EXTRA_COLORS.green:
      return 'green';
    case EXTRA_COLORS.orange:
      return 'orange';
    case EXTRA_COLORS.red:
      return 'red';
    case EXTRA_COLORS.violet:
      return 'violet';
    default:
      return 'grey';
  }
};

export const getDateAndTimeFromISO = (
  isoDateTime: string,
  timeZone: string = '',
) => {
  const datetime = new Date(isoDateTime);
  const timeZoneCode = TIME_ZONE_MAP[timeZone];
  const ts = timeZoneCode ? dayjs.tz(datetime, timeZoneCode) : dayjs(datetime);
  return {
    date: ts.format('MMM DD/YYYY'),
    time: ts.format('hh:mm:ss A z'),
    relative: ts.fromNow(),
  };
};

export const getDashboardOverviewVariation = (
  variationCount: number = 0,
  overviewType: 'positive' | 'negative' | 'neutral',
) => {
  if (overviewType === 'neutral' && variationCount >= 0) {
    return { icon: NeutralIconSvg };
  }
  if (overviewType === 'neutral' && variationCount < 0) {
    return { icon: DecrementBlueSvg };
  }
  if (variationCount >= 0 && overviewType === 'negative') {
    return { icon: IncrementRedSvg };
  }
  if (variationCount < 0 && overviewType == 'negative') {
    return { icon: DecrementRedSvg };
  }
  if (variationCount >= 0 && overviewType === 'positive') {
    return { icon: IncrementGreenSvg };
  } else {
    return { icon: DecrementGreenSvg };
  }
};

export const findIntervalForUsersLogin = (
  usersLoginData: Array<{
    date: string;
    logins: number;
  }>,
  dateRange: string,
) => {
  let intervalRange = 1;
  if (dateRange === '1W') {
    intervalRange = 0;
    return intervalRange;
  }
  if (dateRange === '1M') {
    intervalRange = 3;
    return intervalRange;
  }
  if (dateRange === '3M') {
    intervalRange = 10;
    return intervalRange;
  }
  if (dateRange === '1Y') {
    intervalRange = 30;
    return intervalRange;
  }
  if (dateRange === 'CustomRange' && usersLoginData.length > 30) {
    intervalRange = -1;
    return intervalRange;
  }
  return intervalRange;
};

export const getApps = (
  searchText: string,
  apps: ApplicationSelectCardProps[],
) => {
  let resultList = [];
  const startsWithText = (word: string) =>
    word.toLowerCase().startsWith(searchText.toLowerCase());
  for (let app of apps) {
    const headingWords = app.heading.split(' ');
    if (headingWords.some(startsWithText)) {
      resultList.push(app);
    }
  }
  return resultList;
};

export const getSearchHelpDetails = (
  searchText: string,
  helpDetails: HelpFAQType,
) => {
  let resultList: FAQType[] = [];
  for (let helpItem of helpDetails.faqs) {
    const helpTitle = helpItem.title.toLowerCase();
    if (helpTitle.includes(searchText.trim().toLowerCase())) {
      resultList.push(helpItem);
    }
  }
  return { id: 1, faqs: resultList };
};

export const getFreshToolTipItemList = (
  tooltipItemList: ITooltipValidationParams[],
) => {
  return JSON.parse(JSON.stringify(tooltipItemList));
};

export const getSearchData = (searchText: string, dataToSearch: string[]) => {
  const resultData: string[] = [];
  const startsWithText = (word: string) =>
    word.toLowerCase().startsWith(searchText.toLowerCase());
  for (let item of dataToSearch) {
    if (item.split(' ').some(startsWithText)) {
      resultData.push(item);
    }
  }
  return resultData;
};
export const getNamesFromObject = (listOfObjects: IDropDownItem[]) => {
  return listOfObjects.map((object) => object.name);
};

export const createOperationRequest = (
  operation: OperationTypes,
  value = '',
  path = '/',
) => {
  return {
    op: operation,
    path: path,
    value: value,
  };
};

export const applicationTypeToProtocol = (applicationType: { id: number, name: string }) => {
  switch (applicationType?.name) {
    // Add more application types here.
    case APPLICATION_TYPE_OPTIONS[0].name:
      return APPLICATION_PROTOCOLS.BROWSER_APP;
    case APPLICATION_TYPE_OPTIONS[1].name:
      return APPLICATION_PROTOCOLS.HTTP;
    case APPLICATION_TYPE_OPTIONS[2].name:
      return APPLICATION_PROTOCOLS.SSH;
    case APPLICATION_TYPE_OPTIONS[3].name:
      return APPLICATION_PROTOCOLS.RDP;
    case APPLICATION_TYPE_OPTIONS[4].name:
      return APPLICATION_PROTOCOLS.VNC;

    default:
      return APPLICATION_PROTOCOLS.BROWSER_APP;
  }
};

export const getAuthToken = () => {
  const { token } = store.getState();
  return token?.session?.authToken;
};

export const refreshAuthToken = async (token: any) => {
  await AuthService.refreshToken(token);
};

export const getUserId = () => {
  const { token } = store.getState();
  return token?.session?.username || localStorage.getItem(USER_ID);
};

export const getSource = () => {
  return localStorage.getItem(SOURCE);
};

export const getTimeZone = () => {
  const { header } = store.getState();
  return header?.timeZone;
};

export const getPasswordHash = () => {
  const { token } = store.getState();
  return token?.hash;
}

export const downloadFileFromBlob = (
  blob: Blob,
  filetype: string,
  filename: string,
) => {
  if (!filename || filename.length === 0) {
    filename = 'logs';
  }
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.${filetype}`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const copyText = (text: string, handleCopyCompleted: any) => {
  navigator.clipboard.writeText(text).then(handleCopyCompleted);
};

export const zeroPad = (num: number, minLength: number) => {
  // Convert provided number to string
  let str = num.toString();

  // Add leading zeroes until string is long enough
  while (str.length < minLength) str = '0' + str;

  return str;
};

export const formatTime = (millis: number) => {
  // Calculate total number of whole seconds
  const totalSeconds = Math.floor(millis / 1000);

  // Split into seconds and minutes
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60);

  // Format seconds and minutes as MM:SS
  return zeroPad(minutes, 2) + ':' + zeroPad(seconds, 2);
};

export const formatTimeHoursMinuteSecond = (millis: number) => {
  // Calculate total number of whole seconds
  const totalSeconds = Math.floor(millis / 1000);

  // Split into seconds, minutes and hours
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);

  return (
    zeroPad(hours, 2) + ':' + zeroPad(minutes, 2) + ':' + zeroPad(seconds, 2)
  );
};

export const checkForRelease = () => {
  if (process.env.RELEASE && process.env.RELEASE === 'beta1R1') return true;
  else return false;
};

const isAdmin = () => {
  const role = localStorage.getItem(ROLE) || '';
  return ['admin', 'superadmin'].includes(role?.toLowerCase());
}

export const isAdminRole = (role: string | null): boolean => {
  return Boolean(role && [ADMIN, SECONDARY_ADMIN].includes(role?.toLowerCase()));
};

export const getSideNavItems = () => {
  const role = localStorage.getItem(ROLE) || '';
  if (isAdmin()) {
    if (checkForRelease()) {
      return ADMIN_SIDE_NAVIGATION_ITEMS.filter((data) => data.id !== 4 && data.id !== 12); //disabling policy screen and session hosts for release 1
    }
    return ADMIN_SIDE_NAVIGATION_ITEMS;
  } else {
    return END_USER_SIDE_NAVIGATION_ITEMS;
  }
};

export const getSideNavEndItems = () => {
  const role = localStorage.getItem(ROLE) || '';
  if (isAdmin()) {
    if (checkForRelease()) {
      return SIDE_NAVIGATION_END_ITEMS.filter((data) => data.id !== 8); //disabling settings screen for release 1
    }
    return SIDE_NAVIGATION_END_ITEMS;
  } else {
    return SIDE_NAVIGATION_END_ITEMS;
  }
};

export const getUserFullName = () => {
  return localStorage.getItem(FULL_NAME);
};

//added this so that null doesn't show in profile name
export const getUserFullNameOrUsername = () => {
  return localStorage.getItem(FULL_NAME)
    ? localStorage.getItem(FULL_NAME)
    : getUserId();
};

export const getOrganization = () => {
  const { header } = store.getState();
  return header?.entities?.organizationName;
};

export const getRole = () => {
  const { token } = store.getState();
  return token?.role;
};

export const getHeightOfDetails = (details: any, apps?: boolean) => {
  if (details?.length) {
    if (details.length >= 3) {
      return apps ? 362 : 390;
    } else {
      return 'fit-content';
    }
  }
  return 100;
};

export const getWidthsForPoliciesTable = (
  underTab: string = 'main',
  mainIndex: number,
  indexUsers: number,
  indexUserGroups: number,
) => {
  return underTab === 'main'
    ? POLICIES_HEADERS[mainIndex].width
    : underTab === 'users'
      ? POLICIES_HEADERS_UNDER_USERS[indexUsers].width
      : POLICIES_HEADERS_UNDER_USERGROUPS[indexUserGroups].width;
};

export const handleBackClick = () => {
  history.back();
};

export const handleRefreshClick = () => {
  window.location.reload();
};

export const handleError = (status: any) => {
  const { BACK, REFRESH } = errorMessages;
  if (status === 404) {
    const { HEADING, TEXT, SUB_TEXT } = errorMessages.ERROR_404;
    const imagePath = Error404Icon;
    return {
      imagePath,
      HEADING,
      TEXT,
      SUB_TEXT,
      BACK,
      REFRESH,
      handleBackClick,
      handleRefreshClick,
    };
  } else if (status === 401) {
    const { HEADING, TEXT } = errorMessages.ERROR_401;
    const imagePath = Error401Icon;
    return { imagePath, HEADING, TEXT, BACK, handleBackClick };
  } else if (status === 500) {
    const { HEADING, TEXT, SUB_TEXT } = errorMessages.ERROR_500;
    const imagePath = Error500Icon;
    return {
      imagePath,
      HEADING,
      TEXT,
      SUB_TEXT,
      BACK,
      REFRESH,
      handleBackClick,
      handleRefreshClick,
    };
  } else {
    const { HEADING, TEXT, SUB_TEXT } = errorMessages.GENERIC_ERROR;
    const imagePath = GenericErrorIcon;
    return {
      imagePath,
      HEADING,
      TEXT,
      SUB_TEXT,
      BACK,
      REFRESH,
      handleBackClick,
      handleRefreshClick,
    };
  }
};

export const getPolicyBlocks = (
  searchText: string,
  policyBlocks: IPolicyBlockProps[],
) => {
  let resultList = [];
  const startsWithText = (word: string) =>
    word.toLowerCase().startsWith(searchText.trim().toLowerCase());
  for (let policyBlock of policyBlocks) {
    const childrenWords = policyBlock.children.split(' ');
    if (childrenWords.some(startsWithText)) {
      resultList.push(policyBlock);
    }
  }
  return resultList;
};

export const getChipColorFromLabel = (label: string) => {
  switch (label) {
    case 'Allow':
    case 'allow':
    case 'allow_upload':
    case 'allow_download':
    case 'allow_copy':
    case 'allow_paste':
      return ['green', COMMON_SHADES.green.main];

    case 'Deny':
    case 'deny':
    case 'deny_upload':
    case 'deny_download':
    case 'deny_copy':
    case 'deny_paste':
      return ['red', COMMON_SHADES.red.main];
    case 'Notify':
      return ['violet', COMMON_SHADES.violet.main];

    case 'And':
      return ['blue', COMMON_SHADES.blue.main];

    default:
      if((label||'').toLowerCase().startsWith("allow")) {
        return ['green', COMMON_SHADES.green.main];
      } else if ((label||'').toLowerCase().startsWith("deny")) {
        return ['red', COMMON_SHADES.red.main];
      }
      return ['grey', "#06192d"];
  }
};

export const getDropDownItemToAttachPolicy = (handleAttach: () => void) => {
  const attachPolicy = {
    text: 'Attach Existing Policy',
    icon: AttachIcon,
    activeIcon: AttachActiveIcon,
  };

  return [{ ...attachPolicy, handleClick: handleAttach }];
};

export const getDropDownItemsToAddPolicy = (
  handleAdd: () => void,
  handleClone?: () => void,
  handleAttach?: () => void,
) => {
  const createPolicy = {
    text: 'Create Policy',
    icon: AddIcon,
    activeIcon: AddActiveIcon,
    handleClick: handleAdd,
  };

  const clonePolicy = {
    text: 'Clone Existing Policy',
    icon: CloneIcon,
    activeIcon: CloneActiveIcon,
  };

  const attachPolicy = {
    text: 'Attach Existing Policy',
    icon: AttachIcon,
    activeIcon: AttachActiveIcon,
  };

  if (handleClone && handleAttach) {
    return [
      createPolicy,
      { ...clonePolicy, handleClick: handleClone },
      { ...attachPolicy, handleClick: handleAttach },
    ];
  } else if (handleClone) {
    return [createPolicy, { ...clonePolicy, handleClick: handleClone }];
  } else if (handleAttach) {
    return [createPolicy, { ...attachPolicy, handleClick: handleAttach }];
  } else {
    return [createPolicy];
  }
};

export const getAvgAppsCount = async (
  context: 'usergroup' | 'user',
  contextId: string,
  fromDate: String,
  toDate: String,
  prevFromDate: String,
  prevToDate: String,
) => {
  let avgAppsCount = 0;
  let prevAvgAppsCount = 0;
  if (context !== 'user') {
    avgAppsCount = await UserGroupService.getAverageDailyAppsCount(
      contextId,
      fromDate,
      toDate,
    );
    prevAvgAppsCount = await UserGroupService.getAverageDailyAppsCount(
      contextId,
      prevFromDate,
      prevToDate,
    );
  }
  const diffAvgAppsCount = avgAppsCount - prevAvgAppsCount;
  const avgAppsValues = {
    name: 'Average Daily Applications',
    count: avgAppsCount,
    itemIcon: AppManagementSvg,
    overviewType: diffAvgAppsCount !== 0 && 'neutral',
    variationCount: diffAvgAppsCount !== 0 && diffAvgAppsCount,
  };
  return context === 'user'
    ? avgAppsValues
    : {
      id: 2,
      name: avgAppsValues.name,
      count: avgAppsValues.count,
      overviewItemicon: avgAppsValues.itemIcon,
      itemOverviewType: avgAppsValues.overviewType,
      variationCount: avgAppsValues.variationCount,
    };
};

const getOnlyReadPermissions = (connectionPermissions: any) => {
  let result: any = {};
  for (const appId in connectionPermissions) {
    const permissions: String[] = connectionPermissions[appId];
    if (permissions.includes('READ')) {
      result[appId] = permissions;
    }
  }
  return result;
};

export const filterAppsWithReadPermission = (permissionsData: any) => {
  permissionsData.connectionPermissions = getOnlyReadPermissions(
    permissionsData?.connectionPermissions ?? {}
  );

  return permissionsData;
};

export const getUserNameFromEmail = (email: string) => {
  return email.split('@')[0];
};

export const validateUserNameAndEmail = (users: any, email: string) => {
  return users?.some(
    (user: any) =>
      (user.email && user.email.toLowerCase() === email.toLowerCase()) || user.username === getUserNameFromEmail(email)
  );
};

export const getHeight = (
  height: number,
  type?: 'apps' | 'policies' | 'details',
) => {
  if (type === 'apps') {
    return height - 435;
  } else if (type === 'policies') {
    return height - 430;
  } else if (type === 'details') {
    return height - 345;
  } else {
    return height - 415;
  }
};

export const lockTabSwitch = () => {
  localStorage.setItem('PREVENT_TAB_SWITCH', 'true');
};

export const unlockTabSwitch = () => {
  localStorage.setItem('PREVENT_TAB_SWITCH', 'false');
};

export const isTabSwitchLocked = () => {
  return localStorage.getItem('PREVENT_TAB_SWITCH') === 'true' ? true : false;
};

export const isUsergroupALLUsers = (usergroupName: String) => {
  return (
    usergroupName.toLowerCase().trim() === ALL_USERS_GROUP.toLowerCase().trim()
  );
};

export const formatFileSize = (bytes: number, decimalPoint: number) => {
  if (bytes == 0) return '0 Bytes';
  const k = 1000;
  const dm = decimalPoint || 2;
  const sizes = ['bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
};

export const formatAMPM = (time: string) => {
  const [hhmm, amOrPm] = time.split(' ');
  const [hours, mins] = hhmm.split(':');
  return { hours, mins, amOrPm };
};

export const convertToHHMM = (hours: string, mins: string, amPm: boolean) => {
  const amOrPm = amPm ? 'PM' : 'AM';
  return hours + ':' + mins + ' ' + amOrPm;
};

export const convertEndDateToEOD = (endDate: Date) => {
  // Here we are converting the end date time to 11:59 pm
  const endDateConverted = new Date();
  endDateConverted.setFullYear(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
  );
  endDateConverted.setHours(23);
  endDateConverted.setMinutes(59);
  endDateConverted.setSeconds(59);
  return endDateConverted;
};

export const convertDateWithTimezone = (
  date: string,
  timezoneOption: string,
) => {
  return new Date(
    new Date(date).toLocaleString('en-US', {
      timeZone: timezoneOption,
    }),
  );
};

export const getRequiredTimeZone = (
  date: any,
  timeZoneOption: string,
  target: 'sessions' | 'logs',
) => {
  if (target === 'sessions') {
    if (timeZoneOption === ACCOUNT_SETTING_TIMEZONE_OPTIONS[4].name) {
      return date;
    }
  } else {
    if (typeof date === 'string') {
      date = date + ' UTC';
    } else {
      const requiredDate = new Date(date);
      date = `${requiredDate.getFullYear()}/${requiredDate.getMonth() + 1
        }/${requiredDate.getDate()}-${requiredDate.getHours()}:${requiredDate.getMinutes()}:${requiredDate.getSeconds()}`;
      date = date.toString() + ' UTC';
    }
  }

  const currentDate = convertDateWithTimezone(
    date,
    TIME_ZONE_MAP[timeZoneOption],
  );

  let requiredYear: number = currentDate.getFullYear();
  let requiredMonth: number = currentDate.getMonth() + 1;
  let requiredDate: number = currentDate.getDate();
  let requiredHours: number = currentDate.getHours();
  let requiredMinutes: string | number = currentDate.getMinutes();
  let requiredSeconds: string | number = currentDate.getSeconds();
  const customDate = `${requiredYear}/${requiredMonth}/${requiredDate}`;
  const customTime = `${requiredHours}:${requiredMinutes}:${requiredSeconds}`;
  return `${customDate}-${customTime}`;
};

export const getConditionsDropdownFromConditionBlocks = (
  conditions: IPolicyBlockProps[],
) => {
  const dropdownOptions: IDropdownOptionProps[] = [];
  conditions.forEach((condition, index: number) => {
    let id = index;
    if (condition.elementId) {
      id = parseInt(condition.elementId?.split('_')[1]);
    }
    dropdownOptions.push({ id: id, name: condition.children });
  });
  return dropdownOptions;
};

export const getExpirationStatus = (
  licenseStartDate: Date,
  licenseEndDate: Date,
) => {
  const diffTotalTime = licenseEndDate.getTime() - licenseStartDate.getTime();
  const totalDays = Math.floor(diffTotalTime / (1000 * 3600 * 24)).toFixed(0);
  const currDate = new Date();
  const diffCompletedTime = currDate.getTime() - licenseStartDate.getTime();
  const completedDays = Math.floor(
    diffCompletedTime / (1000 * 3600 * 24),
  ).toFixed(0);
  const daysLeft = Number(totalDays) - Number(completedDays);
  const percent = (100 * daysLeft) / Number(totalDays);
  const progress = 100 - percent;
  return { daysLeft, progress };
};

export const getExpirationProgress = (
  licenseStartDate: Date,
  licenseEndDate: Date,
) => {
  const diffTotalTime = licenseEndDate.getTime() - licenseStartDate.getTime();
  const totalDays = Math.floor(diffTotalTime / (1000 * 3600 * 24)).toFixed(0);
  const currDate = new Date();
  const diffCompletedTime = currDate.getTime() - licenseStartDate.getTime();
  const completedDays = Math.floor(
    diffCompletedTime / (1000 * 3600 * 24),
  ).toFixed(0);
  const leftOverDays = Number(totalDays) - Number(completedDays);
  const percent = 100 - (Number(completedDays) * 100) / Number(totalDays);
  console.log(leftOverDays, percent);
  return { leftOverDays, percent };
};
export const mapPlanWithIcon = (plan: string) => {
  if (plan === 'standard') {
    return StandardSvg;
  } else if (plan === 'advanced') {
    return AdvancedSvg;
  } else if (plan === 'enterprise') {
    return EnterpriseSvg;
  } else {
    return TryAndBuy;
  }
};

/**
 * Added by devang get current date in ISO or DATE OBJ Format
 * PARAM 1: will return ISO 2022-04-30T07:08:49.349Z format
 * PARAM 2: WIll return Date OBJ Sat Apr 30 2022 12:38:49 GMT+0530 (India Standard Time)
 */

export const getCurrentDate = (param: string) => param.length ? new Date(param) : new Date(new Date().toISOString());

export const replacePlaceholders = (str: string, data: any) => str.replace(/{([^}]+)}/g, (_, key) => data[key]);

export const addSpacesForSecretKey = (str: string) => {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    if (i % 4 == 0) {
      result = result.concat(' ');
    }
    result = result.concat(str[i]);
  }
  return result.trim();
};

export const isWindowBlocked = (win: any) => !win || win.closed || typeof win.closed == "undefined";

export const DEFAULT_APP_ICON = 'data:image/svg+xml;base64,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';

export const ICON_FETCH_SERVICE_URL = 'https://us-central1-sonet-icon-service.cloudfunctions.net/icon';

export const isServerApp = (protocol: string) => protocol === APPLICATION_PROTOCOLS.SSH || protocol === APPLICATION_PROTOCOLS.VNC || protocol === APPLICATION_PROTOCOLS.RDP;

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const deleteEmptyProps = (o: any): any => {
  const cleanedObj = { ...o };
  Object.keys(cleanedObj).forEach(k => {
    if (cleanedObj[k] === null || cleanedObj[k] === undefined ||
      (Array.isArray(cleanedObj[k]) && cleanedObj[k].length === 0)) {
      delete cleanedObj[k];
    }
  });
  return cleanedObj;
}

export const generateUUID = () => {
  let d = new Date().getTime();
  let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now()*1000)) || 0;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      let r = Math.random() * 16;
      if(d > 0){
          r = (d + r)%16 | 0;
          d = Math.floor(d/16);
      } else {
          r = (d2 + r)%16 | 0;
          d2 = Math.floor(d2/16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export const createPermissionRequest = (username: string): any => {
  const payload = [{
    "op": "add",
    "path": "/userPermissions/" + username,
    "value": "UPDATE"
  }, {
    "op": "add",
    "path": "/systemPermissions",
    "value": "ADMINISTER"
  }, {
    "op": "add",
    "path": "/systemPermissions",
    "value": "CREATE_USER"
  }, {
    "op": "add",
    "path": "/systemPermissions",
    "value": "CREATE_USER_GROUP"
  }, {
    "op": "add",
    "path": "/systemPermissions",
    "value": "CREATE_CONNECTION"
  }, {
    "op": "add",
    "path": "/systemPermissions",
    "value": "CREATE_CONNECTION_GROUP"
  }, {
    "op": "add",
    "path": "/systemPermissions",
    "value": "CREATE_SHARING_PROFILE"
  }];

  return payload;
}

export const createPermissionDeleteRequest = (username: string): any => {
  const payload = [{
    "op": "add",
    "path": "/userPermissions/" + username,
    "value": "UPDATE"
  }, {
    "op": "remove",
    "path": "/systemPermissions",
    "value": "ADMINISTER"
  }, {
    "op": "remove",
    "path": "/systemPermissions",
    "value": "CREATE_USER"
  }, {
    "op": "remove",
    "path": "/systemPermissions",
    "value": "CREATE_USER_GROUP"
  }, {
    "op": "remove",
    "path": "/systemPermissions",
    "value": "CREATE_CONNECTION"
  }, {
    "op": "remove",
    "path": "/systemPermissions",
    "value": "CREATE_CONNECTION_GROUP"
  }, {
    "op": "remove",
    "path": "/systemPermissions",
    "value": "CREATE_SHARING_PROFILE"
  }];

  return payload;
}

export const createConnectionAndSharingProfilePermissions = async (
  connectionId: string,
  sharingProfiles: any[]
): Promise<IAddOrRemoveAppsToUserRequestBody[]> => {
  const permissions: IAddOrRemoveAppsToUserRequestBody[] = [
    // Connection permissions
    createOperationRequest(
      OperationTypes.add,
      'READ',
      `/connectionPermissions/${connectionId}`
    ),
  ];
  const sharingProfilesArray = Object.values(sharingProfiles);
  // Add sharing profile permissions
  if (Array.isArray(sharingProfilesArray) && sharingProfilesArray.length > 0) {
    sharingProfilesArray.forEach((profile) => {
      console.log("Profile inside forEach:", profile);
      permissions.push(
        createOperationRequest(
          OperationTypes.add,
          'READ',
          `/sharingProfilePermissions/${profile.identifier}`
        )
      );
    });
  }
  console.log("Permisison", permissions);
  return permissions;
};

export const createRemoveConnectionAndSharingProfilePermissions = async (
  connectionId: string,
  sharingProfiles: any[]
): Promise<IAddOrRemoveAppsToUserRequestBody[]> => {
  const permissions: IAddOrRemoveAppsToUserRequestBody[] = [
    // Connection permissions
    createOperationRequest(
      OperationTypes.remove,
      'READ',
      `/connectionPermissions/${connectionId}`
    )
  ];
  console.log("Sharing Profile", sharingProfiles);
  const sharingProfilesArray = Object.values(sharingProfiles);
  console.log("Sharing Profile Array ", sharingProfilesArray);
  // Add sharing profile permissions
  if (Array.isArray(sharingProfilesArray) && sharingProfilesArray.length > 0) {
    sharingProfilesArray.forEach((profile) => {
      console.log("Profile inside forEach:", profile);
      permissions.push(
        createOperationRequest(
          OperationTypes.remove,
          'READ',
          `/sharingProfilePermissions/${profile.identifier}`
        )
      );
    });
  }
  console.log("Permisison Json", permissions);
  return permissions;
};


export const getCustomDomainName = (customDomain: any) => {
  return (customDomain?.path || customDomain?.url) ?? `${customDomain?.hostname}${customDomain?.port ? ':' + customDomain?.port : ''}`;
}

/* Get Application Type: used this function in the [Userdetail and Usergroup detail page] */
export const getApplicationType = (customDomainData: any, applicationType: string) => {
  if(applicationType === APPLICATION_PROTOCOLS.BROWSER_APP) {
    return Boolean(JSON.parse(customDomainData?.isHttpOnly ?? 'false')) ? APPLICATION_TYPE_OPTIONS[1].name : APPLICATION_TYPE_OPTIONS[0].name
  }
  return applicationType;  
}

export const convertedISOFormatted = (inputDateString: Date, flag: 's' | 'e' | null = null) => {
  if (inputDateString) {
    let inputDate = dayjs(inputDateString).utc().add(1, 'day');
    if (flag === 's') {
      inputDate = inputDate.startOf('day');
    } else if (flag === 'e') {
      inputDate = inputDate.endOf('day');
    }
    return inputDate.toISOString();
  }
  return null;
}

/* Sort Table Text Data by asc or desc order */
export const sortTableTextData = (tableTextDataList: any, sortKey: string, sortOrder: 'asc' | 'desc') => {
  return Array.isArray(tableTextDataList) ? tableTextDataList?.sort((firstValue: any, secondValue: any) => {
    const firstVal = (firstValue?.[sortKey] || '')?.toLowerCase();
    const secondVal = (secondValue?.[sortKey] || '')?.toLowerCase();
    if (sortOrder === 'asc') {
      return firstVal.localeCompare(secondVal);
    } else {
      return secondVal.localeCompare(firstVal);
    }
  }) : [];
}

/* Sort Table Time Data by asc or desc order */
export const sortTableTimeData = (tableTextDataList: any, sortKey: string, sortOrder: 'asc' | 'desc') => {
  return Array.isArray(tableTextDataList) ? 
    tableTextDataList
    ?.map((data: any) => ({...data, [sortKey]: data[sortKey] && dayjs(data[sortKey])}))
    ?.sort((firstValue: any, secondValue: any) => {
    if (sortOrder === 'asc') {
      return firstValue[sortKey] - secondValue[sortKey];
    } else {
      return secondValue[sortKey] - firstValue[sortKey];  
    }
  }) : [];
}

/* Sort Table Time Data by duration by asc or desc order */
export const sortTableTimeDurationData = (tableTextDataList: any, sortAKey: string, sortBKey: string, sortOrder: 'asc' | 'desc') => {
  return Array.isArray(tableTextDataList) ? tableTextDataList?.sort((firstValue: any, secondValue: any) => {
    const firstVal = new Date(firstValue?.[sortAKey])?.getTime() - new Date(firstValue?.[sortBKey])?.getTime();
    const secondVal = new Date(secondValue?.[sortAKey])?.getTime() - new Date(secondValue?.[sortBKey])?.getTime();;
    if (sortOrder === 'asc') {
      return firstVal - secondVal;
    } else {
      return secondVal - firstVal;  
    }
  }) : [];
}

export const sortTableNumberData = (
  tableTextDataList: any,
  sortKey: string,
  sortOrder: 'asc' | 'desc',
) => {
  return Array.isArray(tableTextDataList)
    ? tableTextDataList?.sort((firstValue: any, secondValue: any) =>
        sortOrder === 'asc'
          ? firstValue[sortKey] - secondValue[sortKey]
          : secondValue[sortKey] - firstValue[sortKey],
      )
    : [];
};

export const getMimeTypeOfBase64String = (base64Str: string) => {
  var result = null;

  if (typeof base64Str !== 'string') {
    return result;
  }

  var mime = base64Str.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);

  if (mime && mime.length) {
    result = mime[1];
  }

  return result;
}

export const isAllowedImageMimeType = (mimeType: string) => {
  console.log(`${mimeType} is part of ${ALLOWED_IMAGE_MIME_TYPES.includes(mimeType) ? "" : "not"} allowed mime types.`)
  return ALLOWED_IMAGE_MIME_TYPES.includes(mimeType);
}

export function delay(ms: any) {
  const startTime = Date.now();
  while (Date.now() - startTime < ms) {}
}